import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import {
  Bike,
  Component,
  COMPONENT_CATEGORIES,
  blankBike,
  blankComponent,
  normalizeBike,
  groupComponents,
  stockComponents,
  GROUPSETS,
  Groupset,
  fetchPageMeta,
  vallaBike,
  VALLA_NAME,
} from "../COMPONENTS/bikes/bikes";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import "../CSS/bikes.scss";

// Is this "pryl" a bicycle? Only bikes get the drawn schematic + groupset tools.
const BIKE_TYPE = /cyk|bike|mtb|landsväg|landsvag|gravel|road|race|velo|cyclo|\bcx\b|grus|berg|trail|enduro|downhill|\bdh\b|fatbike|hybrid|stig/i;
const isBikeType = (t: string): boolean => BIKE_TYPE.test(t || "");
const SKI_TYPE = /längd|langd|rullskid|rull.?skid|skidor|skida|skate|klassisk|roller.?ski|xc.?ski|pjäx/i;
// Broad category a pryl is filed under on the overview (bikes together, skis
// together, otherwise its own type).
const categoryOf = (t: string): string => {
  if (isBikeType(t)) return "Cyklar";
  if (SKI_TYPE.test(t || "")) return "Skidor";
  return (t || "").trim() || "Övrigt";
};

// Evenly spaced spokes from the hub out to the rim, as one path string.
const spokes = (cx: number, cy: number, r: number, n = 24): string =>
  Array.from({ length: n }, (_, i) => {
    const a = ((2 * Math.PI) / n) * i;
    return `M${cx},${cy} L${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
  }).join(" ");

// Gear teeth: short radial ticks around a circle, for chainrings/cassette/jockeys.
const teeth = (cx: number, cy: number, r: number, n: number, len = 2.4): string =>
  Array.from({ length: n }, (_, i) => {
    const a = ((2 * Math.PI) / n) * i;
    const c = Math.cos(a), s = Math.sin(a);
    return `M${(cx + r * c).toFixed(1)},${(cy + r * s).toFixed(1)} `
      + `L${(cx + (r + len) * c).toFixed(1)},${(cy + (r + len) * s).toFixed(1)}`;
  }).join(" ");

// A frame tube between two points, drawn as a filled quad of the given half-width,
// so the frame reads as real tubing instead of a stick figure.
const tube = (x1: number, y1: number, x2: number, y2: number, w: number): string => {
  const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * w, ny = (dx / len) * w;
  return `M${(x1 + nx).toFixed(1)},${(y1 + ny).toFixed(1)} L${(x2 + nx).toFixed(1)},${(y2 + ny).toFixed(1)} `
    + `L${(x2 - nx).toFixed(1)},${(y2 - ny).toFixed(1)} L${(x1 - nx).toFixed(1)},${(y1 - ny).toFixed(1)} Z`;
};

// A callout label pointing at a part: leader line from the part + text in the
// margin, laid out like a classic labelled bike diagram. `side` picks the column.
type Callout = { cat: string; ax: number; ay: number; ty: number; side: "l" | "r" };

const ROAD_LABELS: Callout[] = [
  { cat: "Sadel", ax: 160, ay: 66, ty: -32, side: "l" },
  { cat: "Sadelstolpe", ax: 171, ay: 86, ty: 2, side: "l" },
  { cat: "Ram", ax: 182, ay: 134, ty: 38, side: "l" },
  { cat: "Bromsar bak", ax: 120, ay: 114, ty: 76, side: "l" },
  { cat: "Bakdäck", ax: 66, ay: 168, ty: 116, side: "l" },
  { cat: "Bakhjul", ax: 92, ay: 150, ty: 152, side: "l" },
  { cat: "Kassett", ax: 120, ay: 168, ty: 188, side: "l" },
  { cat: "Bakväxel", ax: 125, ay: 196, ty: 224, side: "l" },
  { cat: "Kedja", ax: 150, ay: 190, ty: 258, side: "l" },
  { cat: "Stam", ax: 268, ay: 80, ty: -32, side: "r" },
  { cat: "Styre", ax: 289, ay: 92, ty: 2, side: "r" },
  { cat: "Bromsar fram", ax: 288, ay: 114, ty: 40, side: "r" },
  { cat: "Gaffel", ax: 285, ay: 142, ty: 80, side: "r" },
  { cat: "Framdäck", ax: 342, ay: 168, ty: 120, side: "r" },
  { cat: "Framhjul", ax: 314, ay: 150, ty: 158, side: "r" },
  { cat: "Pedaler", ax: 204, ay: 200, ty: 212, side: "r" },
  { cat: "Vevparti", ax: 186, ay: 180, ty: 250, side: "r" },
];

const MTB_LABELS: Callout[] = [
  { cat: "Sadel", ax: 162, ay: 68, ty: -44, side: "l" },
  { cat: "Sadelstolpe", ax: 186, ay: 90, ty: -6, side: "l" },
  { cat: "Ram", ax: 194, ay: 140, ty: 32, side: "l" },
  { cat: "Bakdämpare", ax: 230, ay: 134, ty: 70, side: "l" },
  { cat: "Bromsar bak", ax: 118, ay: 150, ty: 108, side: "l" },
  { cat: "Bromsskivor", ax: 104, ay: 148, ty: 146, side: "l" },
  { cat: "Bakdäck", ax: 46, ay: 166, ty: 184, side: "l" },
  { cat: "Bakhjul", ax: 74, ay: 150, ty: 222, side: "l" },
  { cat: "Kassett", ax: 104, ay: 166, ty: 260, side: "l" },
  { cat: "Bakväxel", ax: 110, ay: 198, ty: 298, side: "l" },
  { cat: "Kedja", ax: 150, ay: 190, ty: 336, side: "l" },
  { cat: "Stam", ax: 298, ay: 72, ty: -44, side: "r" },
  { cat: "Styre", ax: 282, ay: 58, ty: -6, side: "r" },
  { cat: "Växelreglage", ax: 272, ay: 62, ty: 32, side: "r" },
  { cat: "Bromsar fram", ax: 332, ay: 150, ty: 78, side: "r" },
  { cat: "Gaffel", ax: 330, ay: 138, ty: 120, side: "r" },
  { cat: "Framhjul", ax: 362, ay: 150, ty: 170, side: "r" },
  { cat: "Framdäck", ax: 396, ay: 166, ty: 220, side: "r" },
  { cat: "Pedaler", ax: 219, ay: 198, ty: 270, side: "r" },
  { cat: "Vevparti", ax: 198, ay: 178, ty: 312, side: "r" },
];

// Blueprint-style side-view bike; each part is clickable and coloured by whether
// a component is registered for that category (and whether it's been swapped).
// The drawing itself switches between a full-suspension MTB and a road bike so
// it actually resembles the bike in front of you.
const BikeBlueprint = ({ bike, onPart, active }: { bike: Bike; onPart: (cat: string) => void; active: string | null }) => {
  const byCat = new Map<string, Component>();
  bike.components.forEach((c) => { if (!byCat.has(c.category)) byCat.set(c.category, c); });
  const status = (cat: string): string => {
    const c = byCat.get(cat);
    if (!c) return "none";
    if (c.swapped) return "swapped";
    return c.brand || c.name ? "filled" : "empty";
  };
  const P = ({ cat, children }: { cat: string; children: React.ReactNode }) => (
    <g className={`bp-part bp-part--${status(cat)}${active === cat ? " bp-part--active" : ""}`} onClick={() => onPart(cat)}>
      {children}
    </g>
  );
  const Tag = ({ cat, ax, ay, ty, side }: Callout) => {
    const end = side === "l";
    const tx = end ? -60 : 470;
    const lx = end ? -54 : 464;
    return (
      <g className={`bp-part bp-label bp-part--${status(cat)}${active === cat ? " bp-part--active" : ""}`} onClick={() => onPart(cat)}>
        <polyline className="bp-lead" points={`${ax},${ay} ${lx},${ty}`} />
        <circle cx={ax} cy={ay} r="10" className="bp-hotspot" />
        <circle cx={ax} cy={ay} r="2.4" className="bp-dot" />
        <text x={tx} y={ty + 3} textAnchor={end ? "end" : "start"} className="bp-txt">{cat}</text>
      </g>
    );
  };

  const isMtb = /mtb|mountain|terräng|terrang|enduro|trail|downhill|\bdh\b|fatbike|fat\b|stig/i.test(bike.type || "");

  if (isMtb) {
    // ── Full-suspension MTB: knobby tyres, suspension fork, rear shock,
    //    riser bar, dropper post, disc rotors, 1x drivetrain. ──
    return (
      <svg viewBox="-150 -60 760 420" className="bikes__bp-svg">
        <P cat="Bakdäck"><circle cx="104" cy="166" r="60" className="bp-tyre" /><circle cx="104" cy="166" r="53" className="bp-tyre-in" /></P>
        <P cat="Framdäck"><circle cx="338" cy="166" r="60" className="bp-tyre" /><circle cx="338" cy="166" r="53" className="bp-tyre-in" /></P>
        <P cat="Bakhjul">
          <path className="bp-spoke" d={spokes(104, 166, 49)} />
          <circle cx="104" cy="166" r="49" className="bp-line" />
          <circle cx="104" cy="166" r="45" className="bp-rim-in" />
          <circle cx="104" cy="166" r="5" className="bp-line" />
          <circle cx="104" cy="166" r="2" className="bp-dot" />
        </P>
        <P cat="Framhjul">
          <path className="bp-spoke" d={spokes(338, 166, 49)} />
          <circle cx="338" cy="166" r="49" className="bp-line" />
          <circle cx="338" cy="166" r="45" className="bp-rim-in" />
          <circle cx="338" cy="166" r="5" className="bp-line" />
          <circle cx="338" cy="166" r="2" className="bp-dot" />
        </P>
        <P cat="Kedja"><path className="bp-hit" d="M198,164 L104,154 M110,196 L198,188" /><path className="bp-line" d="M198,164 L104,154 M110,196 L198,188" /></P>
        <P cat="Ram">
          <path className="bp-tube" d={tube(198, 178, 104, 166, 4)} />{/* chain stay */}
          <path className="bp-tube" d={tube(190, 108, 104, 166, 3.4)} />{/* seat stay */}
          <path className="bp-tube" d={tube(312, 104, 198, 178, 7)} />{/* down tube */}
          <path className="bp-tube" d={tube(190, 108, 298, 72, 6)} />{/* top tube */}
          <path className="bp-tube" d={tube(198, 178, 190, 108, 5)} />{/* seat tube */}
          <path className="bp-tube" d={tube(298, 72, 312, 104, 6)} />{/* head tube */}
          <circle cx="206" cy="147" r="3" className="bp-line" />{/* swingarm pivot */}
        </P>
        <P cat="Bakdämpare">
          <path className="bp-hit" d="M214,150 L244,122" />
          <path className="bp-shock" d={tube(214, 150, 244, 122, 4.5)} />{/* rear shock */}
          <circle cx="214" cy="150" r="2.5" className="bp-line" />
          <circle cx="244" cy="122" r="2.5" className="bp-line" />
        </P>
        <P cat="Gaffel">
          <path className="bp-tube" d={tube(305, 100, 320, 106, 3.5)} />{/* crown */}
          <path className="bp-tube" d={tube(312, 104, 326, 136, 3)} />{/* stanchion */}
          <path className="bp-fork-leg" d={tube(324, 130, 338, 166, 5)} />{/* fat lowers */}
        </P>
        <P cat="Stam"><path className="bp-hit" d="M298,72 L292,64" /><path className="bp-line" d="M298,72 L292,64" /></P>
        <P cat="Styre">
          <path className="bp-hit" d="M292,64 C290,58 285,55 278,55 L262,55" />
          <path className="bp-line" d="M292,64 C290,58 285,55 278,55 L262,55" />{/* riser bar + grip */}
          <path className="bp-grip" d="M272,55 L262,55" />{/* grip */}
          <path className="bp-line" d="M270,57 q -6,4 -12,3" />{/* brake lever */}
        </P>
        <P cat="Sadelstolpe"><path className="bp-hit" d="M190,108 L185,68" /><path className="bp-line" d="M190,108 L185,68" /><path className="bp-line" d="M187,88 l-3,0" /></P>
        <P cat="Sadel">
          <path className="bp-solid" d="M160,66 C160,62 171,61 185,61 L203,61 C210,61 208,65 202,65 C187,65 173,66 165,68 C161,68 160,68 160,66 Z" />
          <path className="bp-line" d="M185,65 L185,68" />
        </P>
        <P cat="Vevparti">
          <circle cx="198" cy="178" r="14" className="bp-solid" />
          <path className="bp-line" d={teeth(198, 178, 14, 32, 2.2)} />{/* single chainring teeth */}
          <circle cx="198" cy="178" r="2" className="bp-dot" />
          <path className="bp-crank" d="M198,178 L218,198" /><rect x="215" y="194" width="4" height="8" rx="1" transform="rotate(45 217 198)" className="bp-solid" />
        </P>
        <P cat="Pedaler"><rect x="210" y="199" width="17" height="4" rx="1" className="bp-solid" /><path className="bp-line" d="M218,195 L218,199" /></P>
        <P cat="Kassett">
          <circle cx="104" cy="166" r="14" className="bp-line" />
          <path className="bp-line" d={teeth(104, 166, 14, 28, 2)} />
          <circle cx="104" cy="166" r="11" className="bp-rim-in" />
          <circle cx="104" cy="166" r="8" className="bp-rim-in" />
        </P>
        <P cat="Bakväxel">
          <path className="bp-line" d="M104,180 L108,193" />{/* clutch arm */}
          <path className="bp-solid" d="M104,192 l8,-1 l1,10 l-8,1 z" />
          <circle cx="108" cy="194" r="3.2" className="bp-line" /><path className="bp-line" d={teeth(108, 194, 3.2, 9, 1)} />
          <circle cx="109" cy="203" r="3.2" className="bp-line" /><path className="bp-line" d={teeth(109, 203, 3.2, 9, 1)} />
        </P>
        <P cat="Bromsar fram">
          <circle cx="338" cy="166" r="24" className="bp-rotor" />
          <circle cx="338" cy="166" r="8" className="bp-rotor" />
          <rect x="333" y="139" width="9" height="14" rx="2" className="bp-solid" />
          <circle cx="337.5" cy="143" r="0.9" className="bp-dot" /><circle cx="337.5" cy="149" r="0.9" className="bp-dot" />
        </P>
        <P cat="Bromsar bak">
          <circle cx="104" cy="166" r="22" className="bp-rotor" />
          <circle cx="104" cy="166" r="7" className="bp-rotor" />
          <rect x="99" y="141" width="9" height="14" rx="2" className="bp-solid" />
          <circle cx="103.5" cy="145" r="0.9" className="bp-dot" /><circle cx="103.5" cy="151" r="0.9" className="bp-dot" />
        </P>
        {MTB_LABELS.map((l) => <Tag key={l.cat} {...l} />)}
      </svg>
    );
  }

  // ── Road bike: slim tyres, drop bar, double chainring, rim brakes. ──
  // Proportions: wheelbase ≈ 1.5× wheel diameter, top tube ≈ seat tube.
  return (
    <svg viewBox="-150 -50 760 360" className="bikes__bp-svg">
      <P cat="Bakdäck"><circle cx="120" cy="168" r="54" className="bp-tyre" /><circle cx="120" cy="168" r="49" className="bp-tyre-in" /></P>
      <P cat="Framdäck"><circle cx="288" cy="168" r="54" className="bp-tyre" /><circle cx="288" cy="168" r="49" className="bp-tyre-in" /></P>
      <P cat="Bakhjul">
        <path className="bp-spoke" d={spokes(120, 168, 46)} />
        <circle cx="120" cy="168" r="46" className="bp-line" />
        <circle cx="120" cy="168" r="42" className="bp-rim-in" />
        <circle cx="120" cy="168" r="4" className="bp-line" />
        <circle cx="120" cy="168" r="1.8" className="bp-dot" />
      </P>
      <P cat="Framhjul">
        <path className="bp-spoke" d={spokes(288, 168, 46)} />
        <circle cx="288" cy="168" r="46" className="bp-line" />
        <circle cx="288" cy="168" r="42" className="bp-rim-in" />
        <circle cx="288" cy="168" r="4" className="bp-line" />
        <circle cx="288" cy="168" r="1.8" className="bp-dot" />
      </P>
      <P cat="Kedja"><path className="bp-hit" d="M186,167 L120,159 M124,190 L186,191" /><path className="bp-line" d="M186,167 L120,159 M124,190 L186,191" /></P>
      <P cat="Ram">
        <path className="bp-tube" d={tube(186, 180, 120, 168, 2.5)} />{/* chain stay */}
        <path className="bp-tube" d={tube(172, 92, 120, 168, 2.2)} />{/* seat stay */}
        <path className="bp-tube" d={tube(280, 118, 186, 180, 3.6)} />{/* down tube */}
        <path className="bp-tube" d={tube(172, 92, 270, 88, 3.2)} />{/* top tube */}
        <path className="bp-tube" d={tube(186, 180, 172, 92, 3)} />{/* seat tube */}
        <path className="bp-tube" d={tube(270, 88, 280, 118, 4)} />{/* head tube */}
      </P>
      <P cat="Gaffel">
        <path className="bp-tube" d={tube(280, 118, 288, 168, 2.6)} />
        <circle cx="280" cy="118" r="2" className="bp-dot" />{/* fork crown */}
      </P>
      <P cat="Stam"><path className="bp-hit" d="M270,88 L263,77" /><path className="bp-line" d="M270,88 L263,77" /></P>
      <P cat="Styre">
        <path className="bp-hit" d="M263,77 C273,72 285,72 289,81 C293,91 288,101 278,101 C272,101 270,96 273,92" />
        <path className="bp-line" d="M263,77 C273,72 285,72 289,81 C293,91 288,101 278,101 C272,101 270,96 273,92" />
        <path className="bp-line" d="M287,81 q 9,3 6,14" />{/* brake lever hood */}
      </P>
      <P cat="Sadelstolpe"><path className="bp-hit" d="M172,92 L170,72" /><path className="bp-line" d="M172,92 L170,72" /></P>
      <P cat="Sadel">
        <path className="bp-solid" d="M148,72 C148,68 158,67 172,67 L189,67 C197,67 196,71 190,71 C174,71 158,72 152,74 C149,74 148,74 148,72 Z" />
        <path className="bp-line" d="M171,71 L171,73" />{/* rail to post */}
      </P>
      <P cat="Vevparti">
        <circle cx="186" cy="180" r="13" className="bp-solid" />
        <path className="bp-line" d={teeth(186, 180, 13, 30, 2)} />{/* outer chainring teeth */}
        <path className="bp-line" d={teeth(186, 180, 9, 22, 1.6)} />{/* inner chainring teeth */}
        <circle cx="186" cy="180" r="9" className="bp-line" />
        <circle cx="186" cy="180" r="2" className="bp-dot" />
        <path className="bp-crank" d="M186,180 L204,200" /><rect x="201" y="196" width="4" height="8" rx="1" transform="rotate(45 203 200)" className="bp-solid" />
      </P>
      <P cat="Pedaler"><rect x="197" y="201" width="15" height="4" rx="1" className="bp-solid" /><path className="bp-line" d="M204,197 L204,201" /></P>
      <P cat="Kassett">
        <circle cx="120" cy="168" r="10" className="bp-line" />
        <path className="bp-line" d={teeth(120, 168, 10, 24, 1.6)} />
        <circle cx="120" cy="168" r="7.5" className="bp-rim-in" />
        <circle cx="120" cy="168" r="5" className="bp-rim-in" />
      </P>
      <P cat="Bakväxel">
        <path className="bp-line" d="M120,178 L124,190" />{/* upper link */}
        <path className="bp-solid" d="M121,189 l7,-1 l1,9 l-7,1 z" />{/* cage/body */}
        <circle cx="124" cy="191" r="3" className="bp-line" /><path className="bp-line" d={teeth(124, 191, 3, 9, 1)} />
        <circle cx="125" cy="199" r="3" className="bp-line" /><path className="bp-line" d={teeth(125, 199, 3, 9, 1)} />
      </P>
      <P cat="Bromsar fram">
        <path className="bp-line" d="M288,114 L283,127" /><path className="bp-line" d="M288,114 L293,127" />
        <rect x="281" y="125" width="4" height="5" rx="1" className="bp-solid" /><rect x="291" y="125" width="4" height="5" rx="1" className="bp-solid" />
        <circle cx="288" cy="114" r="1.7" className="bp-dot" /><path className="bp-line" d="M288,114 L288,107" />
      </P>
      <P cat="Bromsar bak">
        <path className="bp-line" d="M120,114 L115,127" /><path className="bp-line" d="M120,114 L125,127" />
        <rect x="113" y="125" width="4" height="5" rx="1" className="bp-solid" /><rect x="123" y="125" width="4" height="5" rx="1" className="bp-solid" />
        <circle cx="120" cy="114" r="1.7" className="bp-dot" /><path className="bp-line" d="M120,114 L120,107" />
      </P>
      {ROAD_LABELS.map((l) => <Tag key={l.cat} {...l} />)}
    </svg>
  );
};

export const BikesView = ({ uid }: { uid: string }) => {
  usePageBackground();
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [hydrated, setHydrated] = useState(false);
  // The open pryl lives in the URL (?pryl=id) so the browser back button returns to the list.
  const history = useHistory();
  const location = useLocation();
  const selectedId = new URLSearchParams(location.search).get("pryl");
  const pushedPryl = useRef(false);
  const openPryl = (id: string) => { pushedPryl.current = true; history.push({ search: `?pryl=${encodeURIComponent(id)}` }); };
  const closePryl = () => { if (pushedPryl.current) { pushedPryl.current = false; history.goBack(); } else history.replace({ search: "" }); };
  const [editingBike, setEditingBike] = useState<Bike | null>(null);
  const [editingComp, setEditingComp] = useState<{ bikeId: string; comp: Component; exists: boolean } | null>(null);
  const [groupsetFor, setGroupsetFor] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(() => localStorage.getItem("bikes-edit") === "1");
  const toggleEdit = () => setEditMode((v) => { localStorage.setItem("bikes-edit", v ? "0" : "1"); return !v; });
  const [partCat, setPartCat] = useState<string | null>(null); // blueprint selection
  useEffect(() => setPartCat(null), [selectedId, editMode]);

  useEffect(() => {
    let cancelled = false;
    db.collection("users").doc(uid).get().then((snap) => {
      if (cancelled) return;
      const data = snap.data();
      const loaded = Array.isArray(data?.bikes) ? (data!.bikes as any[]).map(normalizeBike) : [];
      loaded.forEach((b) => { if (b.name === VALLA_NAME && b.notes.startsWith("Vallalager:")) b.notes = ""; });
      loaded.forEach((b) => { if (b.name === VALLA_NAME) b.components.forEach((c) => { if (c.category === "Fästvalla" && c.toBuy && c.name === "Fästvalla" && c.notes.startsWith("Måste köpas")) { c.name = "Ingen vald ännu"; c.toBuy = false; c.notes = ""; } }); });
      // One-time seed of the valla inventory (flag keeps it from coming back after deletion).
      if (data?.vallaSeeded !== true && !loaded.some((b) => b.name === VALLA_NAME)) loaded.push(vallaBike());
      setBikes(loaded);
      setHydrated(true);
    }).catch(() => setHydrated(true));
    return () => { cancelled = true; };
  }, [uid]);

  useEffect(() => {
    if (!hydrated) return;
    const t = window.setTimeout(() => {
      db.collection("users").doc(uid).set({ bikes, vallaSeeded: true }, { merge: true }).catch(() => {});
    }, 700);
    return () => window.clearTimeout(t);
  }, [bikes, hydrated, uid]);

  const selected = useMemo(() => bikes.find((b) => b.id === selectedId) || null, [bikes, selectedId]);

  // Group the prylar by their type/category ("Övrigt" for untyped, sorted last).
  const grouped = useMemo(() => {
    const m = new Map<string, Bike[]>();
    bikes.forEach((b) => {
      const g = categoryOf(b.type);
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(b);
    });
    m.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name, "sv")));
    return Array.from(m.entries()).sort((a, b) =>
      a[0] === "Övrigt" ? 1 : b[0] === "Övrigt" ? -1 : a[0].localeCompare(b[0], "sv")
    );
  }, [bikes]);

  const saveBike = (bk: Bike) => {
    const clean = { ...bk, name: bk.name.trim() || "Namnlös pryl" };
    setBikes((cur) => (cur.some((b) => b.id === clean.id) ? cur.map((b) => (b.id === clean.id ? clean : b)) : [...cur, clean]));
    setEditingBike(null);
  };
  const deleteBike = (id: string) => {
    if (!window.confirm("Ta bort prylen och alla dess komponenter?")) return;
    setBikes((cur) => cur.filter((b) => b.id !== id));
    setEditingBike(null);
    history.replace({ search: "" });
    pushedPryl.current = false;
  };

  const saveComp = (bikeId: string, comp: Component) => {
    const clean = { ...comp, name: comp.name.trim(), category: comp.category.trim() || "Övrigt" };
    setBikes((cur) =>
      cur.map((b) =>
        b.id !== bikeId
          ? b
          : {
              ...b,
              components: b.components.some((c) => c.id === clean.id)
                ? b.components.map((c) => (c.id === clean.id ? clean : c))
                : [...b.components, clean],
            }
      )
    );
    setEditingComp(null);
  };
  const deleteComp = (bikeId: string, compId: string) => {
    setBikes((cur) => cur.map((b) => (b.id === bikeId ? { ...b, components: b.components.filter((c) => c.id !== compId) } : b)));
    setEditingComp(null);
  };

  // Add the standard component slots for the bike type (skip categories already present).
  const addStock = (bike: Bike) => {
    const have = new Set(bike.components.map((c) => c.category));
    const extra = stockComponents(bike.type).filter((c) => !have.has(c.category));
    if (extra.length === 0) return;
    setBikes((cur) => cur.map((b) => (b.id === bike.id ? { ...b, components: [...b.components, ...extra] } : b)));
  };

  // Fill drivetrain/brakes from a groupset preset. Existing components in a
  // category get brand+model (unless the user flagged them as swapped); missing
  // ones are added. Marked as original spec (not swapped).
  const applyGroupset = (bikeId: string, g: Groupset) => {
    setBikes((cur) =>
      cur.map((b) => {
        if (b.id !== bikeId) return b;
        const comps = [...b.components];
        g.parts.forEach(([cat, model]) => {
          const idx = comps.findIndex((c) => c.category === cat);
          if (idx >= 0) {
            if (comps[idx].swapped) return; // keep the user's own swap
            comps[idx] = { ...comps[idx], brand: g.brand, name: model, swapped: false };
          } else {
            comps.push({ ...blankComponent(cat), brand: g.brand, name: model });
          }
        });
        return { ...b, components: comps };
      })
    );
    setGroupsetFor(null);
  };

  return (
    <div className="bikes">
      <div className="bikes__header">
        <NavLink to={selected ? "#" : routeUrls.PROJEKT} className="bikes__back" onClick={(e) => { if (selected) { e.preventDefault(); closePryl(); } }}>
          ← {selected ? "Prylar" : "Projekt"}
        </NavLink>
        <h1 className="bikes__title">{selected ? selected.name : "Mina prylar"}</h1>
        <span className="bikes__sub">{selected ? selected.type || "Komponenter" : "Koll på delarna på dina prylar"}</span>
        <button className={`bikes__mode${editMode ? " bikes__mode--on" : ""}`} onClick={toggleEdit}>
          {editMode ? "✓ Klar" : "✏️ Redigera"}
        </button>
      </div>

      {!selected ? (
        <div className="bikes__cats">
          {grouped.map(([cat, list]) => (
            <div key={cat} className="bikes__cat">
              <h2 className="bikes__cat-title">{cat} <span className="bikes__cat-count">{list.length}</span></h2>
              <div className="bikes__grid">
                {list.map((b) => (
                  <button key={b.id} className="bikes__card" onClick={() => openPryl(b.id)}>
                    <span className="bikes__card-img" style={b.imageUrl ? { backgroundImage: `url(${b.imageUrl})` } : undefined}>
                      {!b.imageUrl && <span className="bikes__card-emoji">{isBikeType(b.type) ? "🚲" : "📦"}</span>}
                    </span>
                    <span className="bikes__card-body">
                      <span className="bikes__card-name">{b.name}</span>
                      <span className="bikes__card-meta">
                        {b.type && <span>{b.type}</span>}
                        <span>{b.components.length} komponenter</span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {editMode && (
            <div className="bikes__grid">
              <button className="bikes__add-card" onClick={() => setEditingBike(blankBike())}>+ Lägg till pryl</button>
            </div>
          )}
          {bikes.length === 0 && !editMode && <p className="bikes__empty">Inga prylar ännu. Tryck ✏️ Redigera för att lägga till.</p>}
        </div>
      ) : (
        <div className="bikes__detail">
          {editMode && (
            <div className="bikes__detail-bar">
              <button className="bikes__btn bikes__btn--ghost" onClick={() => setEditingBike(selected)}>✏️ Redigera pryl</button>
              {isBikeType(selected.type) && <button className="bikes__btn bikes__btn--ghost" onClick={() => addStock(selected)}>🧩 Standard</button>}
              {isBikeType(selected.type) && <button className="bikes__btn bikes__btn--ghost" onClick={() => setGroupsetFor(selected.id)}>⚙️ Gruppset</button>}
              <button className="bikes__btn" onClick={() => setEditingComp({ bikeId: selected.id, comp: blankComponent(), exists: false })}>+ Komponent</button>
            </div>
          )}
          {selected.notes && <p className="bikes__detail-notes">{selected.notes}</p>}

          {!editMode && isBikeType(selected.type) && selected.components.length > 0 && (
            <div className="bikes__blueprint">
              <div className="bikes__bp-hint">Tryck på en del i ritningen för info</div>
              <BikeBlueprint bike={selected} onPart={(cat) => setPartCat((p) => (p === cat ? null : cat))} active={partCat} />
              {partCat && (() => {
                const c = selected.components.find((x) => x.category === partCat);
                const label = c ? [c.brand, c.name].filter(Boolean).join(" ") : "";
                const specs = c ? [c.size, c.weight, c.price, c.date].filter(Boolean) : [];
                return (
                  <div className="bikes__bp-info">
                    <div className="bikes__bp-info-head">
                      <span className="bikes__bp-info-cat">{partCat}{c?.swapped && <span className="bikes__comp-tag">🔧 Bytt</span>}</span>
                      <button className="bikes__modal-close" onClick={() => setPartCat(null)} aria-label="Stäng">×</button>
                    </div>
                    {c ? (
                      <div className="bikes__bp-info-body">
                        {c.image && <img className="bikes__bp-info-img" src={c.image} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />}
                        <div className="bikes__bp-info-text">
                          <div className="bikes__bp-info-name">{label || "Ingen modell angiven"}</div>
                          {specs.length > 0 && <div className="bikes__bp-info-specs">{specs.join(" · ")}</div>}
                          {c.notes && <p className="bikes__bp-info-notes">{c.notes}</p>}
                          {c.link && <a className="bikes__bp-info-link" href={c.link} target="_blank" rel="noopener noreferrer">Öppna produkt →</a>}
                        </div>
                      </div>
                    ) : (
                      <p className="bikes__bp-info-empty">Ingen komponent registrerad här. Byt till ✏️ Redigera för att lägga till.</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {(editMode || !isBikeType(selected.type)) && (selected.components.length === 0 ? (
            editMode ? (
              <div className="bikes__empty">
                <p>Inga komponenter ännu.</p>
                {isBikeType(selected.type) && <button className="bikes__btn" onClick={() => addStock(selected)}>🧩 Fyll i standardkomponenter</button>}
                <p className="bikes__empty-hint">Tryck + Komponent för att lägga till delar.</p>
              </div>
            ) : (
              <div className="bikes__empty"><p>Inga delar ännu. Tryck ✏️ Redigera för att lägga till.</p></div>
            )
          ) : (
            groupComponents(selected.components).map((g) => (
              <div key={g.category} className="bikes__group">
                <h3 className="bikes__group-title">{g.category}</h3>
                <div className="bikes__comp-list">
                  {g.items.map((c) => {
                    const label = [c.brand, c.name].filter(Boolean).join(" ");
                    const specs = [c.size, c.weight, c.price].filter(Boolean);
                    const inner = (
                      <>
                        <span className="bikes__comp-name">
                          {c.image && <img className="bikes__comp-thumb" src={c.image} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />}
                          {c.swapped && <span className="bikes__comp-tag">🔧 Bytt</span>}
                          {c.toBuy && <span className="bikes__comp-tag">🛒 Köpa</span>}
                          {label || <span className="bikes__comp-empty">Original – fyll i</span>}
                          {c.link && (
                            <a className="bikes__comp-link" href={c.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>🔗</a>
                          )}
                        </span>
                        <span className="bikes__comp-meta">
                          {specs.map((s, i) => <span key={i}>{s}</span>)}
                          {c.date && <span className="bikes__comp-date">{c.date}</span>}
                          {c.notes && <span className="bikes__comp-notes">{c.notes}</span>}
                        </span>
                      </>
                    );
                    return editMode ? (
                      <button
                        key={c.id}
                        className={`bikes__comp${c.swapped ? " bikes__comp--swapped" : ""}`}
                        onClick={() => setEditingComp({ bikeId: selected.id, comp: c, exists: true })}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div key={c.id} className={`bikes__comp bikes__comp--static${c.swapped ? " bikes__comp--swapped" : ""}`}>
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ))}
        </div>
      )}

      {editingBike && (
        <BikeEditor
          initial={editingBike}
          exists={bikes.some((b) => b.id === editingBike.id)}
          onSave={saveBike}
          onCancel={() => setEditingBike(null)}
          onDelete={() => deleteBike(editingBike.id)}
        />
      )}

      {groupsetFor && (
        <div className="bikes__overlay" onClick={() => setGroupsetFor(null)}>
          <div className="bikes__modal" onClick={(e) => e.stopPropagation()}>
            <div className="bikes__modal-head">
              <h3 className="bikes__modal-title">Välj gruppset</h3>
              <button className="bikes__modal-close" onClick={() => setGroupsetFor(null)} aria-label="Stäng">×</button>
            </div>
            <div className="bikes__modal-body">
              <p className="bikes__empty-hint" style={{ marginTop: 0 }}>
                Fyller drivlina & bromsar med rätt modellnamn. Delar du markerat som "bytt" rörs inte.
              </p>
              {(["Landsväg", "Gravel", "MTB"] as const).map((t) => (
                <div key={t} className="bikes__gs-group">
                  <h4 className="bikes__gs-title">{t}</h4>
                  {GROUPSETS.filter((g) => g.type === t).map((g) => (
                    <button key={g.id} className="bikes__gs-item" onClick={() => applyGroupset(groupsetFor, g)}>
                      {g.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {editingComp && (
        <ComponentEditor
          initial={editingComp.comp}
          exists={editingComp.exists}
          onSave={(c) => saveComp(editingComp.bikeId, c)}
          onCancel={() => setEditingComp(null)}
          onDelete={() => deleteComp(editingComp.bikeId, editingComp.comp.id)}
        />
      )}
    </div>
  );
};

const BikeEditor = ({
  initial,
  exists,
  onSave,
  onCancel,
  onDelete,
}: {
  initial: Bike;
  exists: boolean;
  onSave: (b: Bike) => void;
  onCancel: () => void;
  onDelete: () => void;
}) => {
  const [b, setB] = useState<Bike>(initial);
  const set = (patch: Partial<Bike>) => setB((cur) => ({ ...cur, ...patch }));
  return (
    <div className="bikes__overlay" onClick={onCancel}>
      <div className="bikes__modal" onClick={(e) => e.stopPropagation()}>
        <div className="bikes__modal-head">
          <h3 className="bikes__modal-title">{exists ? "Redigera pryl" : "Ny pryl"}</h3>
          <button className="bikes__modal-close" onClick={onCancel} aria-label="Stäng">×</button>
        </div>
        <div className="bikes__modal-body">
          <input className="bikes__input" value={b.name} onChange={(e) => set({ name: e.target.value })} placeholder="Namn (t.ex. Neuron, Madshus Redline)" autoFocus />
          <input className="bikes__input" value={b.type} onChange={(e) => set({ type: e.target.value })} placeholder="Typ (t.ex. MTB, Landsväg, Längdskidor)" />
          <input className="bikes__input" value={b.imageUrl} onChange={(e) => set({ imageUrl: e.target.value })} placeholder="🖼️ Bild (länk till bild)" />
          {b.imageUrl && <div className="bikes__img-preview" style={{ backgroundImage: `url(${b.imageUrl})` }} />}
          <textarea className="bikes__textarea" value={b.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Anteckningar (valfritt)" />
        </div>
        <div className="bikes__modal-foot">
          {exists && <button className="bikes__link-btn bikes__link-btn--danger" onClick={onDelete}>Ta bort pryl</button>}
          <span className="bikes__foot-spacer" />
          <button className="bikes__btn bikes__btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="bikes__btn" onClick={() => onSave(b)}>Spara</button>
        </div>
      </div>
    </div>
  );
};

const ComponentEditor = ({
  initial,
  exists,
  onSave,
  onCancel,
  onDelete,
}: {
  initial: Component;
  exists: boolean;
  onSave: (c: Component) => void;
  onCancel: () => void;
  onDelete: () => void;
}) => {
  const [c, setC] = useState<Component>(initial);
  const [fetching, setFetching] = useState(false);
  const [fetchMsg, setFetchMsg] = useState("");
  const set = (patch: Partial<Component>) => setC((cur) => ({ ...cur, ...patch }));

  const importFromLink = async () => {
    if (!c.link.trim()) return;
    setFetching(true);
    setFetchMsg("");
    const meta = await fetchPageMeta(c.link.trim());
    setFetching(false);
    if (!meta) { setFetchMsg("Kunde inte läsa sidan."); return; }
    setC((cur) => ({
      ...cur,
      name: cur.name || meta.title,
      brand: cur.brand || meta.brand,
      price: cur.price || (meta.price ? `${meta.price}${meta.currency ? " " + meta.currency : ""}` : ""),
      image: cur.image || meta.image,
    }));
    if (!meta.title && !meta.image && !meta.price) setFetchMsg("Hittade ingen info på sidan.");
  };

  return (
    <div className="bikes__overlay" onClick={onCancel}>
      <div className="bikes__modal" onClick={(e) => e.stopPropagation()}>
        <div className="bikes__modal-head">
          <h3 className="bikes__modal-title">{exists ? "Redigera komponent" : "Ny komponent"}</h3>
          <button className="bikes__modal-close" onClick={onCancel} aria-label="Stäng">×</button>
        </div>
        <div className="bikes__modal-body">
          <label className="bikes__field">
            <span>Kategori</span>
            <input className="bikes__input" list="bike-categories" value={c.category} onChange={(e) => set({ category: e.target.value })} placeholder="t.ex. Kedja" />
            <datalist id="bike-categories">
              {COMPONENT_CATEGORIES.map((cat) => <option key={cat} value={cat} />)}
            </datalist>
          </label>
          <div className="bikes__form-row">
            <input className="bikes__input" value={c.brand} onChange={(e) => set({ brand: e.target.value })} placeholder="Märke (t.ex. Shimano)" />
            <input className="bikes__input" value={c.name} onChange={(e) => set({ name: e.target.value })} placeholder="Modell (t.ex. XT M8100)" />
          </div>
          <div className="bikes__form-row">
            <input className="bikes__input" value={c.size} onChange={(e) => set({ size: e.target.value })} placeholder="Storlek/spec (t.ex. 11-46, 40 mm)" />
            <input className="bikes__input" value={c.weight} onChange={(e) => set({ weight: e.target.value })} placeholder="Vikt (t.ex. 250 g)" />
          </div>
          <div className="bikes__form-row">
            <input className="bikes__input" value={c.price} onChange={(e) => set({ price: e.target.value })} placeholder="Pris (t.ex. 899 kr)" />
            <input className="bikes__input" value={c.date} onChange={(e) => set({ date: e.target.value })} placeholder="Monterad (t.ex. 2024)" />
          </div>
          <div className="bikes__form-row">
            <input className="bikes__input" value={c.link} onChange={(e) => set({ link: e.target.value })} placeholder="🔗 Länk (produkt/info)" />
            <button className="bikes__btn bikes__btn--ghost" onClick={importFromLink} disabled={fetching || !c.link.trim()}>
              {fetching ? "Hämtar…" : "Hämta info"}
            </button>
          </div>
          {fetchMsg && <span className="bikes__fetch-msg">{fetchMsg}</span>}
          {c.image && (
            <div className="bikes__comp-img-row">
              <img className="bikes__comp-img" src={c.image} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
              <button className="bikes__link-btn" onClick={() => set({ image: "" })}>Ta bort bild</button>
            </div>
          )}
          <label className="bikes__check">
            <input type="checkbox" checked={c.swapped} onChange={(e) => set({ swapped: e.target.checked })} />
            🔧 Bytt / uppgraderad från original
          </label>
          <label className="bikes__check">
            <input type="checkbox" checked={c.toBuy} onChange={(e) => set({ toBuy: e.target.checked })} />
            🛒 Måste köpas
          </label>
          <textarea className="bikes__textarea" value={c.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Anteckningar (t.ex. mil, service)" />
        </div>
        <div className="bikes__modal-foot">
          {exists && <button className="bikes__link-btn bikes__link-btn--danger" onClick={onDelete}>Ta bort</button>}
          <span className="bikes__foot-spacer" />
          <button className="bikes__btn bikes__btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="bikes__btn" onClick={() => onSave(c)}>Spara</button>
        </div>
      </div>
    </div>
  );
};
